const fr = {
  // ── Sections partagées par plusieurs pages ───────────────────────────────
  process: {
    eyebrow: "Notre méthode d'apprentissage",
    title: "Un parcours construit autour de vous",
    aside:
      "Chaque apprenant est différent. Nous partons de votre niveau actuel et construisons un chemin de votre première ligne de code à une réelle aisance sur le terrain.",
    steps: [
      {
        title: "Évaluation",
        body: "Nous évaluons votre niveau actuel et bâtissons le cours autour de vos objectifs, du débutant complet au développeur en poste.",
      },
      {
        title: "Apprentissage",
        body: "Des séances en direct et pratiques, avec un accompagnement en temps réel et des projets tirés de vrais travaux d'ingénierie.",
      },
      {
        title: "Maîtrise",
        body: "Un suivi et un soutien continus, à mesure que vous affinez vos compétences et commencez à les appliquer par vous-même.",
      },
    ],
  },

  whyChoose: {
    eyebrow: "Pourquoi nous choisir",
    title: "La différence tient à notre façon d'enseigner",
    items: [
      {
        title: "Instructeurs experts",
        body: "Des ingénieurs logiciels en activité qui enseignent. Chaque instructeur a livré du code en production et apporte cette expérience en cours.",
      },
      {
        title: "Apprentissage à votre rythme",
        body: "Les cours s'adaptent à vos objectifs et à votre rythme, pour que vous consacriez votre temps aux notions qui vous font avancer.",
      },
      {
        title: "Résultats concrets",
        body: "Les apprenants repartent capables de construire des choses. Le format pratique fait que vous vous exercez à la compétence recherchée, au lieu de seulement en lire la théorie.",
      },
    ],
  },

  // ── Accueil ──────────────────────────────────────────────────────────────
  home: {
    metaTitle: "SkillSynergy | Cours de code et d'IA en direct",
    metaDescription:
      "Des cours de programmation et d'IA en direct et pratiques, pour tous les niveaux. Apprenez le génie logiciel pas à pas avec des instructeurs experts, en groupe ou en individuel.",

    hero: {
      badge: "Cours en direct en ligne",
      titleLine1: "De débutant",
      titleLine2: "à pro. Maîtrisez le",
      titleCoding: "code",
      titleAnd: "&",
      titleAi: "l'IA",
      lead: "Des cours de programmation en direct et pratiques pour tous les niveaux, désormais avec l'IA et l'apprentissage automatique. Apprenez pas à pas avec des instructeurs experts et gagnez une vraie confiance.",
      rating: "Plébiscités par des apprenants du monde entier",
      yearsValue: "10+",
      yearsLabel: "Ans d'expérience",
      imageAlt:
        "Un instructeur guidant un étudiant dans son code sur un ordinateur portable",
    },

    aiSpotlight: {
      badge: "Nouveau cours",
      titlePre: "Apprenez",
      titleAi: "l'IA",
      titlePost: "et l'apprentissage automatique",
      body: "La compétence tech la plus recherchée, enseignée en direct et en pratique. Passez des bases de l'apprentissage automatique à la mise en production d'applications réelles avec des LLMs et des AI agents.",
      exploreCta: "Découvrir le cours d'IA",
    },

    skills: {
      eyebrow: "Ce que vous maîtriserez",
      title: "Des compétences qui bâtissent de vraies carrières",
    },

    pricing: {
      eyebrow: "Tarifs et formules",
      title: "Des formules simples, des progrès sérieux",
    },

    cta: {
      title:
        "Développez de vraies compétences en code avec des cours en direct",
      body: "Réservez une consultation gratuite et nous vous aiderons à choisir le bon chemin.",
    },
  },

  // ── Index des cours ──────────────────────────────────────────────────────
  coursesPage: {
    metaTitle: "SkillSynergy | Cours",
    metaDescription:
      "Des cours de programmation en ligne, personnalisés et en groupe, pour tous les niveaux, à travers des parcours structurés et flexibles d'intensité variable.",

    eyebrow: "Notre catalogue",
    title: "Des cours conçus pour de vrais progrès",
    lead: "Des cours de programmation en ligne, personnalisés et en groupe, pour tous les niveaux, à travers des parcours structurés et flexibles d'intensité variable.",
    statCoursesValue: "7",
    statCoursesLabel: "Cours proposés",
    statYearsValue: "10+",
    statYearsLabel: "Ans d'expérience",
    coreTitle: "Nos cours principaux",
    heroImageAlt:
      "Des étudiants travaillant ensemble sur un exercice de programmation",
  },

  // ── Détail d'un cours ────────────────────────────────────────────────────
  coursePage: {
    curriculumEyebrow: "Programme",
    curriculumTitle: "Ce que vous apprendrez",
    aboutTitle: "À propos de ce cours",
    exploreOthers: "Découvrir d'autres cours",
    readyTitle: (course: string) => `Prêt à commencer ${course} ?`,
    readyBody:
      "Réservez une consultation gratuite et obtenez un plan personnalisé.",
  },

  // ── À propos ─────────────────────────────────────────────────────────────
  about: {
    metaTitle: "SkillSynergy | À propos",
    metaDescription:
      "SkillSynergy anime des cours de programmation en direct et interactifs pour les apprenants de tous niveaux, y compris les grands débutants. Des classes réduites, animées par des ingénieurs en activité.",

    eyebrow: "À propos de SkillSynergy",
    title: "Nous apprenons aux gens à écrire de vrais logiciels",
    lead: "Des cours de programmation en direct et interactifs, conçus pour les apprenants de tous niveaux, même en partant de zéro.",
    storyEyebrow: "À propos de SkillSynergy",
    storyTitle: "Votre guide de confiance vers la programmation",
    storyBody1:
      "SkillSynergy anime des cours de programmation en direct et interactifs pour les apprenants de tous niveaux, y compris les grands débutants. Les classes sont réduites, pratiques et animées par des ingénieurs en activité.",
    storyBody2:
      "L'objectif est simple : vous terminez capable de construire des logiciels par vous-même, et vous avez quelqu'un à qui demander quand vous bloquez.",
    cta: {
      title: "Développons vos compétences ensemble",
      body: "Apprenez aux côtés d'autres personnes, avec des conseils d'experts.",
    },
    imageAlt: [
      "Un étudiant en pair-programming sur un ordinateur portable",
      "Un instructeur relisant du code sur un tableau blanc",
      "Un groupe de travail avançant sur un exercice",
    ],
    storyImageAlt:
      "Un développeur résolvant un problème sur son ordinateur portable",
  },

  // ── Tarifs ───────────────────────────────────────────────────────────────
  pricingPage: {
    metaTitle: "SkillSynergy | Tarifs",
    metaDescription:
      "Choisissez un cours en groupe pour un apprentissage collaboratif, ou l'individuel pour un mentorat entièrement personnalisé. Rythmes Léger, Semi-intensif et Intensif.",

    offerBadge: (offer: string) =>
      `Offre de lancement · ${offer} % de réduction pour les premiers inscrits`,
    title: "Les meilleurs tarifs, pensés pour vous",
    lead: "Choisissez un cours en groupe pour un apprentissage collaboratif, ou l'individuel pour un mentorat entièrement personnalisé.",
    perMonth: "/mois",
    priceNote:
      "Tous les prix s'entendent par mois, TVA incluse le cas échéant. Aucuns frais d'inscription.",
    popular: "Populaire",
    group: "Groupe",
    individual: "Individuel",
    planLabel: "Formule de facturation",
    cta: {
      title: "Vous hésitez sur la formule ?",
      body: "Réservez une consultation gratuite et nous vous recommanderons le rythme adapté à vos objectifs.",
    },
  },

  // ── Contact ──────────────────────────────────────────────────────────────
  contact: {
    metaTitle: "SkillSynergy | Contact",
    metaDescription:
      "Contactez-nous et notre équipe vous aidera à trouver le bon cours et à démarrer. Nous répondons en général sous un jour ouvré.",

    eyebrow: "Prendre contact",
    title: "Envie d'en savoir plus sur nos cours ?",
    lead: "Écrivez-nous et notre équipe vous aidera à trouver le bon cours et à démarrer. Nous répondons en général sous un jour ouvré.",
    phone: "Téléphone",
    email: "E-mail",
    location: "Adresse",
    formTitle: "Envoyez-nous un message",
    fullName: "Nom complet",
    fullNamePlaceholder: "Marie Dupont",
    emailLabel: "E-mail",
    emailPlaceholder: "marie@email.com",
    subject: "Objet",
    subjectPlaceholder: "Demande de renseignements sur un cours",
    message: "Message",
    messagePlaceholder: "Parlez-nous de vos objectifs...",
    send: "Envoyer le message",
    sending: "Envoi…",
    successTitle: "Message envoyé",
    successBody:
      "Merci de nous avoir contactés. Nous répondrons sous un jour ouvré.",
    error: "L'envoi a échoué. Veuillez réessayer ou nous écrire directement.",
  },
};

export default fr;
